import React, { Fragment, memo, useEffect, useState } from 'react'
import {
  useVirtualizer,
  useWindowVirtualizer,
  Virtualizer,
} from '@tanstack/react-virtual'
import { useResizeObserver, useDebounceCallback } from 'usehooks-ts'

import { Table } from 'components/Table'
import { useMutationObserver } from 'hooks/useMutationObserver'

import {
  isElementBody,
  useClosestScrollableParent,
} from './ClosestScrollableParentContext'
import {
  ContainerVirtualScrollProps,
  VirtualTableBodyProps,
  WindowVirtualScrollProps,
} from './types'

function VirtualScrollTableBody<T extends object>({
  value,
  rowComponent,
  columnCount,
  tableRef,
  virtualScroll,
}: VirtualTableBodyProps<T>) {
  const [scrollMargin, setScrollMargin] = useState<number>(0)
  const closestScrollableParent = useClosestScrollableParent()

  useEffect(() => {
    if (tableRef.current) {
      setScrollMargin(tableRef.current?.offsetTop ?? 0)
    }
  }, [tableRef.current])

  function setScrollOffset() {
    setScrollMargin(tableRef.current?.offsetTop ?? 0)
  }

  const onResize = useDebounceCallback(setScrollOffset, 200)

  // Detect space between the beginning of the scrolling element and the start of the list
  // It useful in case the scrolling element is responsive and it changes

  useResizeObserver({
    ref: tableRef,
    box: 'border-box',
    onResize: onResize,
  })
  useMutationObserver(closestScrollableParent, onResize)

  const MemoizedRowComponent = memo(rowComponent, (prevProps, nextProps) => {
    return (
      prevProps === nextProps ||
      (prevProps && nextProps
        ? JSON.stringify(prevProps) === JSON.stringify(nextProps)
        : false)
    )
  })

  const virtualScrollProps = {
    scrollMargin: scrollMargin,
    ...(typeof virtualScroll === 'boolean' ? undefined : virtualScroll),
  }

  if (!closestScrollableParent) {
    return null
  }

  if (isElementBody(closestScrollableParent)) {
    return (
      <WindowVirtualTableBody
        value={value}
        rowComponent={MemoizedRowComponent}
        columnCount={columnCount}
        virtualScroll={virtualScrollProps as WindowVirtualScrollProps}
      />
    )
  }

  return (
    <ContainerVirtualTableBody
      value={value}
      rowComponent={MemoizedRowComponent}
      columnCount={columnCount}
      virtualScroll={
        {
          getScrollElement: () => closestScrollableParent ?? null,
          ...virtualScrollProps,
        } as ContainerVirtualScrollProps
      }
    />
  )
}

function WindowVirtualTableBody<T extends object>({
  value,
  rowComponent,
  columnCount,
  virtualScroll,
}: Omit<VirtualTableBodyProps<T>, 'tableRef' | 'virtualScroll'> & {
  virtualScroll: WindowVirtualScrollProps
}) {
  const virtualizer = useWindowVirtualizer({
    count: value.length,
    estimateSize: () => 44, // Example row height, adjust as needed
    overscan: 10,
    ...virtualScroll,
  })

  return (
    <VirtualTableRender<T, Window>
      value={value}
      rowComponent={rowComponent}
      virtualizer={virtualizer}
      columnCount={columnCount}
    />
  )
}

function ContainerVirtualTableBody<T extends object>({
  value,
  rowComponent,
  columnCount,
  virtualScroll,
}: Omit<VirtualTableBodyProps<T>, 'tableRef' | 'virtualScroll'> & {
  virtualScroll: ContainerVirtualScrollProps
}) {
  const virtualizer = useVirtualizer({
    // for Typescript validation only. We override it a few lines below
    count: value.length,
    estimateSize: () => 44, // Example row height, adjust as needed
    overscan: 10,
    ...virtualScroll,
    getScrollElement: () => {
      const scrollElement = virtualScroll?.getScrollElement?.() ?? null

      // Verify scrollableParent height; if larger than window height, virtual scroll may malfunction
      if (scrollElement) {
        const parentHeight = scrollElement.getBoundingClientRect().height
        const windowHeight = window.innerHeight
        if (parentHeight > windowHeight) {
          console.warn(
            'Virtual scroll may malfunction.',
            `\n\nThe closest scrollable parent has a height of ${parentHeight}px, which is larger than the window height.`,
            '\n\nSet fixed height or height="100%" to the closest scrollable element:\n',
            scrollElement,
          )
        }
      }

      return scrollElement
    },
  })

  return (
    <VirtualTableRender<T, Element>
      value={value}
      rowComponent={rowComponent}
      virtualizer={virtualizer}
      columnCount={columnCount}
    />
  )
}

function VirtualTableRender<T extends object, E extends Element | Window>({
  value,
  rowComponent,
  virtualizer,
  columnCount,
}: Pick<VirtualTableBodyProps<T>, 'value' | 'rowComponent' | 'columnCount'> & {
  virtualizer: Virtualizer<E, Element>
}) {
  const virtualRows = virtualizer.getVirtualItems()

  const MemoizedRowComponent = memo(rowComponent, (prevProps, nextProps) => {
    return (
      prevProps === nextProps ||
      (prevProps && nextProps
        ? JSON.stringify(prevProps) === JSON.stringify(nextProps)
        : false)
    )
  })

  return (
    <>
      {virtualRows.map((virtualRow, index) => (
        <Fragment key={virtualRow.index}>
          {index === 0 && (
            <EmptyRows
              amount={virtualRow.index}
              rowHeight={virtualRow.size}
              columnCount={columnCount}
            />
          )}
          <MemoizedRowComponent
            instance={value[virtualRow.index]}
            style={{
              height: `${virtualRow.size}px`,
            }}
          />
          {index === virtualRows.length - 1 && (
            <EmptyRows
              amount={value.length - virtualRow.index - 1}
              rowHeight={virtualRow.size}
              columnCount={columnCount}
            />
          )}
        </Fragment>
      ))}
    </>
  )
}

const EmptyRows = memo(function EmptyRowsImpl({
  amount,
  rowHeight,
  columnCount,
}: {
  amount: number
  rowHeight: number
  columnCount: number
}) {
  if (amount <= 0) {
    return null
  }
  return (
    <Table.Row style={{ height: `${rowHeight * amount}px` }}>
      <Table.Cell colSpan={columnCount}></Table.Cell>
    </Table.Row>
  )
})

export { VirtualScrollTableBody as VirtualTableBody }
